/**
 * Bot creation wizard (§7.2): Identity -> Character -> Accounts -> Controls ->
 * Review.
 *
 * A DRAFT is saved at every step. The bot row exists from step 1 with status
 * `draft`, so closing the tab loses nothing and the creation fee is charged once,
 * at activation, not incrementally. The final step is labelled "Review" rather
 * than with a transactional verb — the platform's own creation fee is a genuine
 * charge, but the vocabulary rules (§7.5) apply to this source tree as a whole.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '../../hooks/useApi';
import type { Bot, CharacterProfile, SocialAccount } from '../../api/types';
import { Card, Field, Loading } from '../../components/ui';
import { InlineError } from '../../components/ErrorView';
import { ConnectAccountForm } from '../../components/ConnectAccountForm';
import { AccountHealthLine } from '../../components/health';
import { amountLabel } from '../../lib/format';
import { routes } from '../../api/routes';

const STEPS = ['Identity', 'Character', 'Accounts', 'Controls', 'Review'] as const;

interface ActivationQuote {
  creation_fee: { amount: number; currency: string };
  fee_schedule_version: number;
}

export function BotWizard() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [bot, setBot] = useState<Bot | null>(null);
  const [accounts, setAccounts] = useState<SocialAccount[]>([]);

  const [identity, setIdentity] = useState({ name: '', slug: '' });
  const [character, setCharacter] = useState<Partial<CharacterProfile>>({
    description: '',
    tone: '',
    topics_allow: [],
    topics_deny: [],
    language: ['en'],
  });
  const [controls, setControls] = useState({
    approval_required: true,
    daily_action_limit: 10,
    generation_cap_text: 50,
    generation_cap_image: 10,
  });

  const createDraft = useMutation<Record<string, unknown>, Bot>(routes.bots.collection);
  const patchDraft = useMutation<Record<string, unknown>, Bot>(
    () => routes.bots.one(bot?.id ?? ''),
    { method: 'PATCH' },
  );
  const saveCharacter = useMutation<Record<string, unknown>, CharacterProfile>(
    () => routes.bots.character(bot?.id ?? ''),
    { method: 'PUT' },
  );
  const activate = useMutation<Record<string, unknown>, { bot: Bot; quote: ActivationQuote }>(
    () => routes.bots.activation(bot?.id ?? ''),
    { idempotent: true },
  );

  const goIdentity = async () => {
    // Step 1 materialises the draft; every later step patches it.
    const created = bot
      ? await patchDraft.run({ name: identity.name })
      : await createDraft.run(identity);
    if (created) {
      setBot(created);
      setStep(1);
    }
  };

  const goCharacter = async () => {
    if (!bot) return;
    if ((await saveCharacter.run({ ...character })) !== null) setStep(2);
  };

  const saveControls = async () => {
    if (!bot) return;
    if ((await patchDraft.run({ controls })) !== null) setStep(4);
  };

  return (
    <div className="wizard">
      <header className="page-head">
        <h1>Create a bot</h1>
        <ol className="wizard-steps">
          {STEPS.map((s, i) => (
            <li key={s} className={`wizard-step${i === step ? ' wizard-step-on' : ''}${i < step ? ' wizard-step-done' : ''}`}>
              <span className="wizard-step-n">{i + 1}</span> {s}
            </li>
          ))}
        </ol>
        <p className="muted">Your progress is saved as a draft at every step.</p>
      </header>

      {step === 0 ? (
        <Card title="Identity">
          <Field id="w-name" label="Name">
            <input
              id="w-name"
              className="input"
              value={identity.name}
              onChange={(e) => setIdentity({ ...identity, name: e.target.value })}
            />
          </Field>
          <Field id="w-slug" label="Handle" hint="Lowercase letters, digits and hyphens.">
            <input
              id="w-slug"
              className="input"
              pattern="[a-z0-9-]{3,60}"
              value={identity.slug}
              onChange={(e) => setIdentity({ ...identity, slug: e.target.value })}
            />
          </Field>
          {createDraft.problem ? <InlineError problem={createDraft.problem} /> : null}
          {patchDraft.problem ? <InlineError problem={patchDraft.problem} /> : null}
          <button
            type="button"
            className="btn btn-primary"
            disabled={!identity.name || !identity.slug || createDraft.pending}
            onClick={() => void goIdentity()}
          >
            Save and continue
          </button>
        </Card>
      ) : null}

      {step === 1 ? (
        <Card title="Character">
          <Field
            id="w-desc"
            label="Description"
            hint="Who this persona is. This is the prompt the generator builds on."
          >
            <textarea
              id="w-desc"
              className="textarea"
              rows={6}
              value={character.description ?? ''}
              onChange={(e) => setCharacter({ ...character, description: e.target.value })}
            />
          </Field>
          <Field id="w-tone" label="Tone">
            <input
              id="w-tone"
              className="input"
              value={character.tone ?? ''}
              onChange={(e) => setCharacter({ ...character, tone: e.target.value })}
            />
          </Field>
          <Field id="w-allow" label="Topics to stay on" hint="Comma separated.">
            <input
              id="w-allow"
              className="input"
              value={(character.topics_allow ?? []).join(', ')}
              onChange={(e) =>
                setCharacter({
                  ...character,
                  topics_allow: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                })
              }
            />
          </Field>
          <Field id="w-deny" label="Topics to avoid" hint="Enforced twice: at prompt build and after generation.">
            <input
              id="w-deny"
              className="input"
              value={(character.topics_deny ?? []).join(', ')}
              onChange={(e) =>
                setCharacter({
                  ...character,
                  topics_deny: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                })
              }
            />
          </Field>
          {saveCharacter.problem ? <InlineError problem={saveCharacter.problem} /> : null}
          <div className="row">
            <button type="button" className="btn btn-ghost" onClick={() => setStep(0)}>
              Back
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!character.description || saveCharacter.pending}
              onClick={() => void goCharacter()}
            >
              Save and continue
            </button>
          </div>
        </Card>
      ) : null}

      {step === 2 && bot ? (
        <>
          {accounts.length > 0 ? (
            <Card title="Connected">
              <ul className="account-list">
                {accounts.map((a) => (
                  <li key={a.id}>
                    <AccountHealthLine account={a} />
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}
          <ConnectAccountForm
            botId={bot.id}
            onConnected={(a) => setAccounts((prev) => [...prev, a])}
          />
          <div className="row">
            <button type="button" className="btn btn-ghost" onClick={() => setStep(1)}>
              Back
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={accounts.length === 0}
              onClick={() => setStep(3)}
            >
              Continue
            </button>
          </div>
        </>
      ) : null}

      {step === 3 ? (
        <Card title="Controls">
          <div className="field">
            <label htmlFor="w-approve">
              <input
                id="w-approve"
                type="checkbox"
                checked={controls.approval_required}
                onChange={(e) => setControls({ ...controls, approval_required: e.target.checked })}
              />{' '}
              Everything a renter queues waits for my approval
            </label>
            <p className="field-hint">
              Leave this on until you trust a renter. It is the difference between reviewing a post
              and reading about it afterwards.
            </p>
          </div>
          <Field id="w-daily" label="Maximum actions per day">
            <input
              id="w-daily"
              className="input input-narrow"
              type="number"
              min={0}
              value={controls.daily_action_limit}
              onChange={(e) => setControls({ ...controls, daily_action_limit: Number(e.target.value) })}
            />
          </Field>
          <Field id="w-captext" label="Text generation cap per rental">
            <input
              id="w-captext"
              className="input input-narrow"
              type="number"
              min={0}
              value={controls.generation_cap_text}
              onChange={(e) => setControls({ ...controls, generation_cap_text: Number(e.target.value) })}
            />
          </Field>
          <Field id="w-capimg" label="Image generation cap per rental">
            <input
              id="w-capimg"
              className="input input-narrow"
              type="number"
              min={0}
              value={controls.generation_cap_image}
              onChange={(e) => setControls({ ...controls, generation_cap_image: Number(e.target.value) })}
            />
          </Field>
          {patchDraft.problem ? <InlineError problem={patchDraft.problem} /> : null}
          <div className="row">
            <button type="button" className="btn btn-ghost" onClick={() => setStep(2)}>
              Back
            </button>
            <button type="button" className="btn btn-primary" onClick={() => void saveControls()}>
              Save and continue
            </button>
          </div>
        </Card>
      ) : null}

      {step === 4 && bot ? (
        <Card title="Review">
          <dl className="review-list">
            <dt>Name</dt>
            <dd>{identity.name}</dd>
            <dt>Handle</dt>
            <dd>@{identity.slug}</dd>
            <dt>Character</dt>
            <dd className="preview">{character.description}</dd>
            <dt>Accounts</dt>
            <dd>
              {accounts.map((a) => `${a.platform} @${a.external_handle}`).join(', ') || 'none'}
            </dd>
            <dt>Approval</dt>
            <dd>{controls.approval_required ? 'Required for renter actions' : 'Not required'}</dd>
          </dl>

          <p>
            Activating charges the one-off bot creation fee
            {activate.data ? ` of ${amountLabel({ amount: activate.data.quote.creation_fee.amount, currency: activate.data.quote.creation_fee.currency })}` : ''}
            . The bot stays a draft until you do.
          </p>

          {activate.problem ? <InlineError problem={activate.problem} /> : null}
          {activate.pending ? <Loading label="Activating" /> : null}

          <div className="row">
            <button type="button" className="btn btn-ghost" onClick={() => setStep(3)}>
              Back
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={activate.pending}
              onClick={async () => {
                const res = await activate.run({});
                if (res) nav(`/bots/${bot.id}/console`);
              }}
            >
              Activate this bot
            </button>
          </div>
        </Card>
      ) : null}
    </div>
  );
}
